"use client";

import { useEffect, useRef } from "react";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import type { EstaidEvent } from "./events";
import { track } from "./track";

type PageViewLocale = Extract<EstaidEvent, { name: "page_view" }>["props"]["locale"];

/**
 * Fires `page_view` once per pathname change.
 *
 * Why we don't use Amplitude's `pageViews: true` autocapture: the SDK
 * listens for `popstate` + `history.pushState` patches, which aren't
 * reliably triggered by Next.js App Router's RSC navigations. The
 * symptom is missed events on every soft navigation. `usePathname()`
 * is the official hook for "current route", so we just watch it.
 *
 * Edge cases:
 *   - Initial mount fires once (when `lastPath.current` is null).
 *   - Repeated useEffect runs with the same path (StrictMode dev
 *     double-invoke, layout re-renders) are deduped via the ref.
 *   - Fires even before the SDK is initialized: `track()` queues until
 *     init and identity are settled (or drops everything when disabled),
 *     so the first page view of a hard load is not lost.
 */
const PERSON_OVERVIEW_ID = /^(\/overview\/person\/)[^/]+/;

/**
 * Person ids are personal data, so the person-overview segment is masked
 * before the path leaves the browser. BFE and CVR numbers are kept — they
 * identify properties and companies.
 */
export function redactPagePath(path: string): string {
    return path.replace(PERSON_OVERVIEW_ID, "$1[id]");
}

export function usePageViewTracking(): void {
    const pathname = usePathname();
    const locale = useLocale() as PageViewLocale;
    const lastPath = useRef<string | null>(null);

    useEffect(() => {
        if (!pathname) return;
        if (lastPath.current === pathname) return;

        lastPath.current = pathname;
        track("page_view", { path: redactPagePath(pathname), locale });
    }, [pathname, locale]);
}
